import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../supabaseClient';
import { theme } from '../../styles/theme';

const ChecklistLookup = ({ 
  value, 
  onChange, 
  error, 
  required = false,
  onChecklistSelect = () => {},
  className = '' 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedChecklist, setSelectedChecklist] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    // Load initial checklist if value is provided
    if (value) {
      fetchChecklistById(value);
    } else {
      setSelectedChecklist(null);
      setSearchTerm('');
    }
  }, [value]);

  useEffect(() => {
    // Handle click outside to close dropdown
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchChecklistById = async (id) => {
    if (!id) return;
    
    try {
      const { data, error } = await supabase
        .from('audit_checklist')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        console.error('Error fetching checklist:', error);
        throw error;
      }

      if (data) {
        setSelectedChecklist(data);
        setSearchTerm(`${data.revision_number} - ${data.title}`);
      } else {
        // Clear selection if no data found
        setSelectedChecklist(null);
        setSearchTerm('');
        onChange(null);
      }
    } catch (error) {
      console.error('Error fetching checklist:', error);
      // Clear selection on error
      setSelectedChecklist(null);
      setSearchTerm('');
      onChange(null);
    }
  };

  const searchChecklists = async (term) => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('audit_checklist')
        .select('*')
        .or(`revision_number.ilike.%${term}%,title.ilike.%${term}%,description_short.ilike.%${term}%`)
        .order('revision_number', { ascending: false })
        .limit(10);

      if (error) throw error;
      setChecklists(data || []);
      setShowDropdown(true);
    } catch (error) {
      console.error('Error searching checklists:', error);
      setChecklists([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    
    // Clear selection if input is cleared
    if (!term) {
      setSelectedChecklist(null);
      onChange(null);
      setChecklists([]);
      setShowDropdown(false);
      return;
    }

    if (term.length >= 2) {
      searchChecklists(term);
    } else {
      setChecklists([]);
      setShowDropdown(false);
    }
  };

  const handleChecklistSelect = (checklist) => {
    if (!checklist || !checklist.id) {
      setSelectedChecklist(null);
      setSearchTerm('');
      onChange(null);
    } else {
      setSelectedChecklist(checklist);
      setSearchTerm(`${checklist.revision_number} - ${checklist.title}`);
      onChange(checklist.id);
      onChecklistSelect(checklist);
    }
    setShowDropdown(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Audit Checklist
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <input
        type="text"
        value={searchTerm}
        onChange={handleInputChange}
        className={`
          w-full px-4 py-2.5 rounded-lg
          border ${error ? 'border-red-500' : 'border-gray-300'}
          focus:outline-none focus:ring-2 focus:ring-opacity-50
          ${error ? 'focus:ring-red-500' : `focus:ring-[${theme.colors.primary.main}]`}
          ${error ? 'focus:border-red-500' : `focus:border-[${theme.colors.primary.main}]`}
        `}
        placeholder="Search by revision number or title"
      />
      {loading && (
        <div className="absolute right-3 top-9">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900"></div>
        </div>
      )}
      {showDropdown && checklists.length > 0 && (
        <div className="absolute z-10 w-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 max-h-60 overflow-auto">
          {checklists.map((checklist) => (
            <button
              key={checklist.id}
              onClick={() => handleChecklistSelect(checklist)}
              className="w-full px-4 py-2 text-left hover:bg-gray-100 focus:outline-none"
            >
              <div className="font-medium">Rev. {checklist.revision_number}</div>
              <div className="text-sm">{checklist.title}</div>
              <div className="text-xs text-gray-500">
                Created: {formatDate(checklist.created_at)}
              </div>
            </button>
          ))}
        </div>
      )}
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
      {selectedChecklist && (
        <div className="mt-2 text-sm text-gray-600">
          <div>Description: {selectedChecklist.description_short}</div>
          {selectedChecklist.pdf_document_link && (
            <a
              href={selectedChecklist.pdf_document_link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 hover:underline"
            >
              View Checklist PDF
            </a>
          )}
        </div>
      )}
    </div>
  );
};

export default ChecklistLookup;
